import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { Upload, CheckCircle2, Loader2, Activity, Download, Briefcase, AlertCircle } from 'lucide-react';
import Papa, { ParseResult } from 'papaparse';
import { Button } from '../../components/ui/Button';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { normalizeOrg } from '../../lib/utils';

type WizardStep = 'upload' | 'mapping' | 'reconciliation' | 'importing' | 'complete';

interface CSVRow { [key: string]: string; }

interface ReconciledRow {
  csvRow: CSVRow;
  matchType: 'new' | 'exact';
  existingId: string | null;
  matchedProgramId: string | null;
  manualProgramId?: string | null;
}

const INITIAL_MAPPING = {
  name: '',
  program: '',
  tracking_mode: '',
  start_date: '',
  end_date: '',
  status: ''
};

export const EngagementImportWizard: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<WizardStep>('upload');
  const [parsedData, setParsedData] = useState<CSVRow[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>(INITIAL_MAPPING);
  const [reconciliation, setReconciliation] = useState<{ new: number; matched: number; rows: ReconciledRow[] }>({ new: 0, matched: 0, rows: [] });
  const [importResults, setImportResults] = useState({ success: 0, updated: 0, errors: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const { data: allPrograms } = useQuery({
    queryKey: ['all-programs-import-lookup'],
    queryFn: async () => {
      const { data } = await supabase.from('programs').select('id, name').order('name');
      return (data || []).map(p => ({ id: p.id, name: p.name }));
    }
  });

  const downloadTemplate = () => {
    const headers = ['Name', 'Program', 'Tracking Mode', 'Start Date', 'End Date', 'Status'];
    const csv = Papa.unparse({ fields: headers, data: [] });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'engagements_import_template.csv');
    link.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      Papa.parse(e.target.files[0], {
        header: true,
        skipEmptyLines: true,
        complete: (results: ParseResult<CSVRow>) => {
          const headers = results.meta.fields || [];
          const newMapping = { ...INITIAL_MAPPING };
          headers.forEach(h => {
            const norm = h.toLowerCase();
            if (norm.includes('name') || norm.includes('title')) newMapping.name = h;
            else if (norm.includes('prog')) newMapping.program = h;
            else if (norm.includes('mode') || norm.includes('track')) newMapping.tracking_mode = h;
            else if (norm.includes('start')) newMapping.start_date = h;
            else if (norm.includes('end')) newMapping.end_date = h;
            else if (norm.includes('status')) newMapping.status = h;
          });
          setMapping(newMapping);
          setParsedData(results.data as CSVRow[]);
          setStep('mapping');
        }
      });
    }
  };

  const startReconciliation = async () => {
    setIsProcessing(true);
    const names = Array.from(new Set(parsedData.map(r => r[mapping.name]?.trim()).filter(Boolean)));
    const progNames = Array.from(new Set(parsedData.map(r => r[mapping.program]?.trim()).filter(Boolean)));
    
    const [engsRes, progsRes] = await Promise.all([
      supabase.from('engagements').select('id, name').in('name', names),
      supabase.from('programs').select('id, name').in('name', progNames)
    ]);

    const engMap = new Map<string, string>(engsRes.data?.map(e => [e.name.toLowerCase().trim(), e.id]) || []);
    const progMap = new Map<string, string>(progsRes.data?.map(p => [p.name.toLowerCase().trim(), p.id]) || []);

    const reconciledRows: ReconciledRow[] = parsedData.map(row => {
      const name = row[mapping.name]?.toLowerCase().trim();
      const prog = row[mapping.program]?.toLowerCase().trim();
      const existingId = name ? engMap.get(name) : null;
      return {
        csvRow: row,
        matchType: (existingId ? 'exact' : 'new') as 'exact' | 'new',
        existingId: existingId || null,
        matchedProgramId: prog ? (progMap.get(prog) || null) : null
      };
    });

    setReconciliation({ 
      new: reconciledRows.filter(r => r.matchType === 'new').length,
      matched: reconciledRows.filter(r => r.matchType === 'exact').length,
      rows: reconciledRows 
    });
    setIsProcessing(false);
    setStep('reconciliation');
  };

  const runImport = async () => {
    setIsProcessing(true);
    setStep('importing');
    setImportError(null);
    let success = 0, updated = 0, errors = 0;

    for (const item of reconciliation.rows) {
      try {
        const progId = item.manualProgramId || item.matchedProgramId || (allPrograms?.[0]?.id) || null;
        if (!item.csvRow[mapping.name]?.trim() || !progId) throw new Error("Missing Identity");

        const payload = {
          name: item.csvRow[mapping.name]?.trim(),
          program_id: progId,
          tracking_mode: (item.csvRow[mapping.tracking_mode]?.toLowerCase() || 'roster') as any,
          start_date: item.csvRow[mapping.start_date] || null,
          end_date: item.csvRow[mapping.end_date] || null,
          status: item.csvRow[mapping.status] || 'in development',
          created_by_email: profile?.email
        };

        if (item.matchType === 'exact' && item.existingId) {
          const { error } = await supabase.from('engagements').update(payload).eq('id', item.existingId);
          if (error) throw error;
          updated++;
        } else {
          const { error } = await supabase.from('engagements').insert([payload]);
          if (error) throw error;
          success++;
        }
      } catch (err) {
        console.error("Engagement Row Failure:", err);
        errors++;
      }
    }

    setImportResults({ success, updated, errors });
    setStep('complete');
    setIsProcessing(false);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-24 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight">Engagement Portfolio Sync</h1>
          <p className="text-slate-500 font-bold text-[10px] uppercase tracking-widest mt-1">Bulk Strategic Initiative Onboarding</p>
        </div>
        <button onClick={() => navigate('/admin')} className="text-[10px] font-black uppercase text-slate-400 hover:text-slate-900 tracking-widest">ABORT SYNC</button>
      </div>

      <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden min-h-[500px] flex flex-col">
        {step === 'upload' && (
          <div className="flex-1 p-20 flex flex-col items-center justify-center text-center">
            <Upload className="text-slate-100 mb-6 animate-bounce" size={64} />
            <h3 className="text-xl font-black text-slate-900 uppercase">Load Engagement CSV</h3>
            <div className="flex flex-col items-center gap-4 mt-8">
              <label className="cursor-pointer px-12 py-5 bg-slate-900 text-[#FFDB00] rounded-md font-black uppercase text-[10px] tracking-widest hover:bg-black transition-all shadow-xl">
                Select Dataset
                <input type="file" className="hidden" accept=".csv" onChange={handleFileChange} />
              </label>
              <button onClick={downloadTemplate} className="flex items-center gap-2 px-6 py-3 text-slate-500 hover:text-slate-900 text-[10px] font-black uppercase tracking-widest transition-colors">
                <Download size={14} /> Download Template
              </button>
            </div>
          </div>
        )}

        {step === 'mapping' && (
          <div className="p-10 space-y-8">
             <div className="space-y-1">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">FIELD ALIGNMENT</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Align Columns to Portfolio Registry</p>
             </div>
             <div className="grid grid-cols-1 gap-2">
               {Object.keys(INITIAL_MAPPING).map(field => (
                  <div key={field} className="flex items-center justify-between p-4 bg-slate-50 rounded-md border border-slate-100">
                    <span className="font-black text-[11px] text-slate-900 uppercase">{field.replace('_', ' ')}</span>
                    <select value={mapping[field]} onChange={(e) => setMapping(prev => ({ ...prev, [field]: e.target.value }))} className="bg-white border border-slate-200 rounded px-4 py-2 text-[11px] font-bold w-64 shadow-inner">
                      <option value="">-- Unmapped --</option>
                      {Object.keys(parsedData[0] || {}).map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
               ))}
             </div>
             <div className="flex justify-between items-center pt-8 border-t border-slate-50">
               <Button variant="ghost" onClick={() => setStep('upload')}>Back</Button>
               <Button onClick={startReconciliation} disabled={!mapping.name || isProcessing} isLoading={isProcessing}>START RECONCILIATION</Button>
             </div>
          </div>
        )}

        {step === 'reconciliation' && (
          <div className="p-10 space-y-8 overflow-visible">
             <div className="flex items-center justify-between">
                <h3 className="text-sm font-black text-slate-900 uppercase">Portfolio Reconciliation</h3>
                <div className="flex gap-2">
                   <div className="px-3 py-1 bg-emerald-50 text-emerald-600 border-emerald-100 rounded text-[9px] font-black uppercase">{reconciliation.new} NEW</div>
                   <div className="px-3 py-1 bg-blue-50 text-blue-600 border-blue-100 rounded text-[9px] font-black uppercase">{reconciliation.matched} UPDATES</div>
                </div>
             </div>

             {importError && (
               <div className="p-4 bg-rose-50 border border-rose-100 rounded-lg flex items-start gap-3 text-rose-600 animate-in slide-in-from-top-2">
                 <AlertCircle size={18} className="shrink-0 mt-0.5" />
                 <div className="space-y-1">
                   <p className="text-[10px] font-black uppercase">Database Rejection</p>
                   <p className="text-[11px] font-bold leading-tight">{importError}</p>
                 </div>
               </div>
             )}

             <div className="overflow-visible border border-slate-200 rounded-md">
                <table className="w-full text-left border-collapse table-fixed">
                  <thead className="sticky top-0 bg-slate-900 text-[#FFDB00] text-[9px] font-black uppercase tracking-widest z-20">
                    <tr>
                      <th className="px-6 py-4 w-1/3">Engagement Definition</th>
                      <th className="px-6 py-4 w-1/3">Program Alignment</th>
                      <th className="px-6 py-4 w-1/3 text-right">Impact</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {reconciliation.rows.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4">
                          <p className="font-black uppercase text-slate-900 text-[11px] leading-none mb-1 truncate">{r.csvRow[mapping.name]}</p>
                          <p className="text-slate-500 font-bold text-[10px] truncate">{r.csvRow[mapping.tracking_mode] || 'Roster'} Mode</p>
                        </td>
                        <td className="px-6 py-4 overflow-visible">
                           <SearchableSelect 
                             options={allPrograms || []}
                             value={r.manualProgramId || r.matchedProgramId || ''}
                             onChange={(val) => {
                               const newRows = [...reconciliation.rows];
                               newRows[i].manualProgramId = val;
                               setReconciliation({ ...reconciliation, rows: newRows });
                             }}
                             placeholder="Select Program..."
                           />
                        </td>
                        <td className="px-6 py-4 text-right">
                           <span className="text-slate-900 font-black uppercase text-[10px] tracking-widest">
                              {r.matchType === 'new' ? 'Portfolio Entry' : 'Strategic Refresh'}
                           </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
             </div>
             <div className="flex gap-4 pt-6 border-t border-slate-50">
                <Button variant="outline" onClick={() => setStep('mapping')} className="flex-1">Back</Button>
                <Button onClick={runImport} className="flex-[2] py-4 shadow-xl" icon={<Activity size={18} />}>COMMIT TO PORTFOLIO</Button>
             </div>
          </div>
        )}

        {step === 'importing' && (
          <div className="flex-1 p-20 text-center flex flex-col items-center justify-center">
            <Loader2 className="animate-spin text-slate-900 mb-6" size={64} />
            <h3 className="text-xl font-black text-slate-900 uppercase">Updating Strategic Initiatives</h3>
          </div>
        )}

        {step === 'complete' && (
          <div className="flex-1 p-20 text-center flex flex-col items-center justify-center animate-in zoom-in-95 duration-300">
            <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mb-8 shadow-inner"><CheckCircle2 size={40} /></div>
            <h3 className="text-3xl font-black text-slate-900 uppercase mb-8 tracking-tight">Sync Complete</h3>
            <div className="grid grid-cols-2 gap-4 w-full max-w-lg mb-12 mx-auto">
              <div className="p-8 bg-slate-50 rounded border border-slate-100 text-center">
                <p className="text-4xl font-black text-slate-900">{importResults.success}</p>
                <p className="text-[10px] font-black uppercase text-slate-400 mt-2">NEW ENGAGEMENTS</p>
              </div>
              <div className="p-8 bg-slate-50 rounded border border-slate-100 text-center">
                <p className="text-4xl font-black text-slate-900">{importResults.updated}</p>
                <p className="text-[10px] font-black uppercase text-slate-400 mt-2">UPDATED</p>
              </div>
            </div>
            <Button onClick={() => navigate('/admin')} className="px-16 py-5 shadow-2xl">RETURN TO ADMIN</Button>
          </div>
        )}
      </div>
    </div>
  );
};